import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import SEO from '@/components/SEO';
import Breadcrumb from '@/components/Breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';

// Hardcoded defaults used ONLY if the "Blog" page record or its page_header section
// is missing in the CMS. Editable values live in /admin/pages → Blog → Sekcije → Header stranice.
const FALLBACK = {
    eyebrow: 'Savjeti i članci',
    heading: 'Blog Škobić Keramike',
    subheading: 'Korisne informacije o keramičarskim radovima u Istri.',
    background_image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=2000&q=80',
    image_alt: 'Blog',
};

export default function BlogList() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageMeta, setPageMeta] = useState(null);
    useEffect(() => {
        api.get('/public/blog').then(({ data }) => setPosts(data)).catch(() => {}).finally(() => setLoading(false));
        // Load the "Blog" page record so the hero is editable from CMS (Header stranice section)
        api.get('/public/page/blog').then(({ data }) => setPageMeta(data)).catch(() => {});
    }, []);

    // Pull values from page_header section (CMS) with fallback to defaults
    const header = (pageMeta?.sections || []).find((s) => s.type === 'page_header' && s.enabled !== false);
    const hd = header?.data || {};
    const eyebrow = hd.eyebrow || FALLBACK.eyebrow;
    const heading = hd.heading || pageMeta?.h1 || FALLBACK.heading;
    const subheading = hd.subheading || FALLBACK.subheading;
    const bgImage = hd.background_image || FALLBACK.background_image;
    const imgAlt = hd.image_alt || FALLBACK.image_alt;
    const seo = pageMeta?.seo || {};

    return (
        <>
            <SEO
                title={seo.meta_title || pageMeta?.title || 'Blog – Savjeti o keramici | Škobić Keramika'}
                description={seo.meta_description || pageMeta?.description || 'Korisni savjeti o ugradnji keramike, adaptacijama kupaonica, hidroizolaciji i izboru materijala.'}
                image={seo.og_image || pageMeta?.featured_image}
                noindex={seo.robots_index === false}
                nofollow={seo.robots_follow === false}
            />
            <section className="relative overflow-hidden border-b border-border">
                <img src={bgImage} alt={imgAlt} className="absolute inset-0 h-full w-full object-cover" fetchpriority="high" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/45 to-black/65" />
                <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-36 lg:pb-24">
                    {eyebrow && <p className="text-xs uppercase tracking-[0.18em] text-white/85 mb-4">{eyebrow}</p>}
                    <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight">{heading}</h1>
                    {subheading && <p className="mt-4 text-white/85 text-lg max-w-2xl">{subheading}</p>}
                </div>
            </section>

            <div className="sk-container pt-6">
                <Breadcrumb items={[{ name: 'Blog', url: '/blog' }]} />
            </div>

            <section className="sk-section">
                <div className="sk-container">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[1,2,3].map((i) => <Skeleton key={i} className="aspect-[16/10] rounded-2xl" />)}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {posts.map((post) => (
                                <Link key={post.id} to={`/blog/${post.slug}`} className="group overflow-hidden rounded-2xl border border-border bg-white" data-testid="blog-card">
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img src={post.featured_image} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" />
                                    </div>
                                    <div className="p-6">
                                        {post.categories?.[0] && (
                                            <Badge variant="secondary" className="bg-[color:var(--sk-surface-2)] text-foreground/80">{post.categories[0]}</Badge>
                                        )}
                                        <h2 className="mt-3 font-display text-xl sm:text-2xl tracking-tight">{post.title}</h2>
                                        <p className="mt-2 text-sm text-foreground/70 line-clamp-3">{post.excerpt}</p>
                                        <p className="mt-5 text-xs uppercase tracking-[0.18em] text-foreground/55">{formatDate(post.published_at)}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
